// src/stores/tickets.js

import { writable } from 'svelte/store';
import { browser } from '$app/environment';
import posthog from 'posthog-js';

const LS_KEY_PREFIX = 'tickets_';

// ───────────────────────────────────────────────────────────────────────────────
//  Default fallback limits: PID → max tickets per user.
//  If PostHog doesn’t return valid JSON, we’ll use these defaults.
//  Adjust as needed to match your original behavior (all “1”).
// ───────────────────────────────────────────────────────────────────────────────
const DEFAULT_LIMITS = {
  '3iMDUr3IXFf6smyj5kEk': 1,
  'Bu9ylwIF7oXc0q7JLyWQ': 1,
  'KX0GXok8OQtZoulm4XSd': 1,
  'Bqe3IoMgRCwZ2XsqdyaG': 1,
  'KPsy3OiBKGb1e5Oih4Lj': 1,
  'VC4VdNc0h2dz7IRDIdw5': 1
};

// ───────────────────────────────────────────────────────────────────────────────
//  Export the original MAX_LIMIT constant for backward compatibility.
//  Other parts of your UI/components still refer to MAX_LIMIT = 1.
// ───────────────────────────────────────────────────────────────────────────────
export const MAX_LIMIT = 1;

/**
 * fetchTicketLimitsFlag()
 *
 * 1) If not in a browser or PostHog isn’t initialized, return DEFAULT_LIMITS.  
 * 2) Check posthog.isFeatureEnabled('ticket_limits'). If false, return DEFAULT_LIMITS.  
 * 3) Otherwise, call posthog.getFeatureFlagPayload('ticket_limits') to get the raw payload.  
 *    • If payload is null/undefined/empty string, fall back to DEFAULT_LIMITS.  
 *    • If payload is already an object, merge it over DEFAULT_LIMITS.  
 *    • If payload is a string, attempt JSON.parse.  
 *    • If parsing fails, fall back to DEFAULT_LIMITS.  
 */
function fetchTicketLimitsFlag() {
  if (!browser || typeof posthog === 'undefined') {
    console.warn('[tickets.js] Not in browser or PostHog not available; using DEFAULT_LIMITS.');
    return { ...DEFAULT_LIMITS };
  }

  if (!posthog.isFeatureEnabled('ticket_limits')) {
    console.log('[tickets.js] ticket_limits flag is disabled or missing; using DEFAULT_LIMITS.');
    return { ...DEFAULT_LIMITS };
  }

  const rawPayload = posthog.getFeatureFlagPayload('ticket_limits');
  console.log('[tickets.js] ticket_limits rawPayload =>', rawPayload);

  if (rawPayload == null || rawPayload === '') {
    console.warn('[tickets.js] ticket_limits payload is empty; using DEFAULT_LIMITS.');
    return { ...DEFAULT_LIMITS };
  }

  if (typeof rawPayload === 'object' && !Array.isArray(rawPayload)) {
    return { ...DEFAULT_LIMITS, ...rawPayload };
  }

  try {
    const parsed = JSON.parse(rawPayload);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return { ...DEFAULT_LIMITS, ...parsed };
    } else {
      console.warn('[tickets.js] ticket_limits parsed to non-object; using DEFAULT_LIMITS.');
      return { ...DEFAULT_LIMITS };
    }
  } catch (err) {
    console.warn('[tickets.js] ticket_limits payload is not valid JSON; using DEFAULT_LIMITS.', err);
    return { ...DEFAULT_LIMITS };
  }
}

/**
 * removeObsoleteLocalStorageEntries(limitMap)
 *
 * Scans localStorage for keys starting with LS_KEY_PREFIX. If the PID portion
 * is not present in limitMap, remove that key from localStorage.
 */
function removeObsoleteLocalStorageEntries(limitMap) {
  if (!browser) return;
  Object.keys(localStorage)
    .filter((key) => key.startsWith(LS_KEY_PREFIX))
    .forEach((key) => {
      const pid = key.slice(LS_KEY_PREFIX.length);
      if (!(pid in limitMap)) {
        localStorage.removeItem(key);
        console.log(`[tickets.js] Removed obsolete localStorage entry: ${key}`);
      }
    });
}

// ───────────────────────────────────────────────────────────────────────────────
//  Module‐scope: build initial limitsByPid & limitedPids. On SSR (browser=false),
//  we simply keep DEFAULT_LIMITS. In the browser, fetchTicketLimitsFlag() runs.
//  Then immediately remove outdated localStorage entries.
// ───────────────────────────────────────────────────────────────────────────────
let limitsByPid = { ...DEFAULT_LIMITS };
let limitedPids = Object.keys(DEFAULT_LIMITS);

if (browser) {
  limitsByPid = fetchTicketLimitsFlag();
  limitedPids = Object.keys(limitsByPid).filter(
    (pid) => Number.isInteger(limitsByPid[pid]) && limitsByPid[pid] > 0
  );
  removeObsoleteLocalStorageEntries(limitsByPid);
}

/**
 * initializeTickets(limitMap)
 *
 *  • First, remove any obsolete localStorage entries again (in case something
 *    was added between the module‐load cleanup and now).  
 *  • Reads localStorage keys that start with LS_KEY_PREFIX.  
 *  • If a stored PID is in limitMap, clamp its count to limitMap[pid].  
 *  • Otherwise, keep the stored count (unlimited).  
 *  • Then make sure every PID in limitMap appears with at least “0” in tickets/localStorage.
 */
function initializeTickets(limitMap) {
  const tickets = {};

  // 1) Remove obsolete entries again to be safe
  removeObsoleteLocalStorageEntries(limitMap);

  // 2) Read existing keys
  if (browser) {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(LS_KEY_PREFIX))
      .forEach((key) => {
        const pid = key.slice(LS_KEY_PREFIX.length);
        const count = parseInt(localStorage.getItem(key), 10);
        if (!isNaN(count)) {
          if (pid in limitMap) {
            tickets[pid] = Math.min(count, limitMap[pid]);
          } else {
            // If somehow an entry slipped through, we’ve already removed it above;
            // so this branch should rarely run. But if it does, preserve the value.
            tickets[pid] = count;
          }
        }
      });
  }

  // 3) Ensure every PID in limitMap has at least 0
  Object.keys(limitMap).forEach((pid) => {
    if (!(pid in tickets)) {
      tickets[pid] = 0;
      if (browser) {
        localStorage.setItem(`${LS_KEY_PREFIX}${pid}`, '0');
      }
    }
  });

  return tickets;
}

/**
 * createTicketsStore(limitMap)
 *
 * Returns a Svelte store with methods:
 *  • getCount(pid)    – returns current count for that PID  
 *  • setCount(pid,n)  – clamps `n` against limitMap[pid], persists to localStorage  
 *  • addCount(pid,inc)– increments then clamps/persists
 */
function createTicketsStore(limitMap = {}) {
  const initialTickets = initializeTickets(limitMap);
  const store = writable(initialTickets);

  function getCount(pid) {
    let count = 0;
    store.subscribe((tickets) => {
      count = tickets[pid] || 0;
    })();
    return count;
  }

  function setCount(pid, newCount) {
    let finalCount = newCount;
    if (pid in limitMap) {
      finalCount = Math.min(newCount, limitMap[pid]);
    }
    store.update((tickets) => {
      tickets[pid] = finalCount;
      if (browser) {
        localStorage.setItem(`${LS_KEY_PREFIX}${pid}`, finalCount.toString());
      }
      return tickets;
    });
  }

  function addCount(pid, increment) {
    const current = getCount(pid);
    setCount(pid, current + increment);
  }

  return {
    subscribe: store.subscribe,
    getCount,
    setCount,
    addCount
  };
}

// ───────────────────────────────────────────────────────────────────────────────
//  Create a single store instance using our initial `limitsByPid` map.
//  Also export `LIMITED_PIDS` (array of capped PIDs) and `LIMITS_BY_PID` (the map).
// ───────────────────────────────────────────────────────────────────────────────
export const ticketsStore = createTicketsStore(limitsByPid);
export const LIMITED_PIDS = limitedPids;
export const LIMITS_BY_PID = limitsByPid;

// ───────────────────────────────────────────────────────────────────────────────
//  Re‐evaluate whenever feature flags reload (e.g. you republish a new JSON payload).
//  posthog.onFeatureFlags() fires on any flag update (including JSON flags).
//  We then re-fetch the JSON, clamp any existing counts above the new caps, cleanup localStorage,
//  and update our module‐scope `limitsByPid` + `limitedPids` for future calls.
// ───────────────────────────────────────────────────────────────────────────────
if (browser && typeof posthog.onFeatureFlags === 'function') {
  posthog.onFeatureFlags(() => {
    const newLimits = fetchTicketLimitsFlag();

    // Remove any localStorage entries not in newLimits
    removeObsoleteLocalStorageEntries(newLimits);

    // Clamp existing counts if they exceed new caps:
    Object.keys({ ...limitsByPid, ...newLimits }).forEach((pid) => {
      const cap = Number.isInteger(newLimits[pid]) ? newLimits[pid] : undefined;
      if (cap !== undefined) {
        const current = ticketsStore.getCount(pid);
        if (current > cap) {
          ticketsStore.setCount(pid, cap);
        }
        if (cap === 0) {
          // If cap is 0, force‐zero that PID
          ticketsStore.setCount(pid, 0);
        }
      }
    });

    // Update module‐scope references
    limitsByPid = newLimits;
    limitedPids = Object.keys(limitsByPid).filter(
      (pid) => Number.isInteger(limitsByPid[pid]) && limitsByPid[pid] > 0
    );
  });
}
